import { useState, useRef, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import Select from 'components/Inputs/Select';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Overlay from 'react-bootstrap/Overlay';
import { getChosenBusiness } from 'store/authentication/selectors';
import { useSelector } from 'react-redux';
import { webApplicationTrackerEmbed } from 'utility/embed';
import { SettingsContentCard } from 'components/Card/Card';
import { PageSizing } from 'components/Layout/PageContentWidth';
import Cols from 'components/Layout/Cols';
import MultiSelect from 'components/Inputs/MultiSelect';
import LongText from 'components/Inputs/LongText';
import Notice from 'components/Kizen/Notice';
import { SubSectionWithHeader as SubSection } from 'components/Layout/SubSection';
import InputControl from 'components/Inputs/InputControl';
import {
  MISSING_URL_PROTOCOL_MESSAGE,
  MISSING_URL_TLD_MESSAGE,
} from 'constants/errorMessages';
import { gutters } from 'app/spacing';
import KizenTypography from 'app/kizentypo';
import useField from 'hooks/useField';
import {
  ACTIVITY_NOTIFICATION_EMAIL,
  ACTIVITY_NOTIFICATION_SMS,
} from 'services/ActivityService';
import SurveyService from 'services/SurveyService';
import { StyledSettingsContentCard } from '../styles';
import SurveyNameInput from './components/SurveyNameInput';
import { checkUrl } from 'utility/validate';
import { toastVariant, useToast } from 'components/ToastProvider';
import { allowNavigation } from 'components/Modals/presets/ConfirmNavigation';
import RedirectUrlInput from 'components/Inputs/RedirectUrlInput';
import {
  getInitialEmailSubscribers,
  getInitialSmsSubscribers,
} from 'utility/team';
import {
  Entities,
  useSelectTypeaheadWithScroll,
} from 'components/Inputs/Select/hooks';
import { convertToTeamOption } from 'utility/TransformToSelectOptions';
import { getOriginalError } from 'services/AxiosService';
import { useAsync } from 'react-use';
import TextInput from 'components/Inputs/TextInput';
import { Description } from 'pages/Common/components/Description';
import Switch from 'components/Kizen/Switch';
import ErrorCard from 'components/Kizen/ErrorCard';
import { surveysQuery } from 'query/forms';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import { useSurveySettingsPage, SUBMISSION_ACTIONS } from '../../context';
import { StaticLink } from 'components/Inputs/StaticText';

const NAME_NAVIGATE_AWAY_MESSAGE = (t) =>
  t('Please name your survey prior to leaving this page.');
const REDIRECT_URL_NAVIGATE_AWAY_MESSAGE_EMPTY = (t) =>
  t('Please enter a valid URL prior to leaving this page.');
const NO_NAME_MESSAGE = (t) => t('Please name your survey.');
const REQUIRED_REDIRECT_URL_MESSAGE = (t) => t('Redirect URL is required.');
const AVAILABILITY_ACTION = {
  GO_TO_NOT_FOUND: 'go_to_not_found',
  GO_TO_URL: 'go_to_url',
  GO_TO_FORM: 'go_to_form',
};

const submissionOptions = (t) => [
  {
    label: t('Go to Thank You Page'),
    value: SUBMISSION_ACTIONS.PAGE,
  },
  {
    label: t('Go to URL'),
    value: SUBMISSION_ACTIONS.URL,
  },
];

const StyledCols = styled(Cols)`
  .InputControl {
    margin-bottom: ${gutters.spacing(3, 4)}px;
  }
`;
const StyleInputControlLink = styled(InputControl)`
  margin-bottom: ${gutters.spacing(3, 4)}px;
`;

const StyledLongText = styled(LongText)`
  & div > div {
    min-height: 218px;
  }
`;
const formatError = (error = {}) => {
  const { response } = error;
  if (!response || response.status === 404) {
    return new Error(
      'Something went wrong. Check your network connection and try again.'
    );
  }
  // check for name exist error
  if (response.data && response.data.errors) {
    const { errors } = response.data;
    if (errors.redirect_url && errors.redirect_url[0]) {
      return new Error(errors.redirect_url[0]);
    }
    if (errors?.name?.length) {
      return new Error(errors.name[0]);
    }
    if (errors?.description?.length) {
      return new Error(errors.description[0]);
    }
  }
  return new Error(
    `${response.statusText} -- ${response.data ? response.data.message : ''}`
  );
};

const SettingsPage = ({ survey, fetchSurvey }) => {
  const { t } = useTranslation();

  const {
    errorMessages,
    submissionAction,
    setErrorMessages,
    setSubmissionAction,
  } = useSurveySettingsPage();

  const business = useSelector(getChosenBusiness);
  const emailSelectRef = useRef();
  const smsSelectRef = useRef();
  const [name, setName] = useField(survey.name);
  const [passVariablesOnRedirect, setPassVariablesOnRedirect] = useField(
    survey.passVariablesOnRedirect
  );

  const [showToast] = useToast();
  const availabilityTargetUrlRef = useRef(null);
  const [availability, setAvailability] = useState({
    enabled: survey.enabled,
    disabledAction: survey.disabledAction,
    disabledTargetForm: survey.disabledTargetForm,
    disabledTargetFormSearch: '',
    disabledTargetUrl: survey.disabledTargetUrl,
  });

  // TODO: when hosting standalone surveys we'll want to use the configurable subdomain from Settings -> Business Information
  const chosenBusinessUrl = `https://${import.meta.env.VITE_EMBED_FRONTEND_DOMAIN}`;
  // const chosenBusinessUrl = window.location.origin;
  // const chosenBusinessUrl = window.location.origin;
  // const chosenBusinessUrl = useSelector(
  //   ({ authentication }) =>
  //     !!authentication.chosenBusiness && authentication.chosenBusiness.url
  // );

  const [redirectUrl, setRedirectUrl] = useField(survey.redirectUrl);
  const [redirectParameterFieldIds, setRedirectParameterFieldIds] = useState(
    survey.redirectParameterFields.map(({ id }) => id)
  );
  const [targetFormValidate, setTargetFormValidate] = useState({
    showMessage: false,
    message: '',
  });
  const [emailTeamMembers, setEmailTeamMembers] = useState(() =>
    getInitialEmailSubscribers(survey.subscribers)
  );
  const [smsTeamMembers, setSmsTeamMembers] = useState(() =>
    getInitialSmsSubscribers(survey.subscribers)
  );

  const previewLink = `${chosenBusinessUrl}/survey/${survey.slug}`;

  const surveyOptionsQuery = useQuery(
    surveysQuery({
      params: {
        enabled: true,
        page_size: FORCE_ALL_RECORDS_SIZE,
        search: availability.disabledTargetFormSearch,
      },
      enabled: availability.disabledAction === AVAILABILITY_ACTION.GO_TO_FORM,
      staleTime: 0,
      select: (data) => {
        const showMissingOptionError =
          Boolean(availability.disabledTargetForm) &&
          !availability.disabledTargetFormSearch &&
          !data.results.some(
            (item) => item.id === availability.disabledTargetForm
          );

        const options = data.results.reduce((acc, item) => {
          if (item.id !== survey.id) {
            acc.push({ value: item.id, label: item.name });
          }
          return acc;
        }, []);

        return {
          options,
          showMissingOptionError,
        };
      },
    })
  );

  useEffect(() => {
    if (surveyOptionsQuery.data?.showMissingOptionError) {
      setTargetFormValidate({
        showMessage: true,
        message: t('The survey was disabled or deleted'),
      });

      const timeout = setTimeout(() => {
        setTargetFormValidate({ showMessage: false, message: '' });
      }, 3000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [surveyOptionsQuery.data?.showMissingOptionError, t]);

  const { value: fields, loading: fieldsLoading } = useAsync(async () => {
    return SurveyService.getFormFields(survey.id);
  }, [survey.id]);

  const handleRedirectUrlChange = async () => {
    const url = redirectUrl.trim();
    setErrorMessages((prev) => ({ ...prev, redirectUrl: '' }));

    if (url.length === 0) {
      setErrorMessages((prev) => ({
        ...prev,
        redirectUrl: REQUIRED_REDIRECT_URL_MESSAGE(t),
      }));
    } else if (!(url.startsWith('http://') || url.startsWith('https://'))) {
      setErrorMessages((prev) => ({
        ...prev,
        redirectUrl: MISSING_URL_PROTOCOL_MESSAGE(t),
      }));
    } else if (!checkUrl(url)) {
      setErrorMessages((prev) => ({
        ...prev,
        redirectUrl: MISSING_URL_TLD_MESSAGE(t),
      }));
    } else {
      try {
        await SurveyService.update(survey.id, {
          redirectUrl: url,
          submissionAction,
        });
      } catch (error) {
        if (error.isAxiosError) {
          const err = formatError(error);
          setErrorMessages((prev) => ({ ...prev, redirectUrl: err.message }));
        }
      } finally {
        await fetchSurvey();
      }
    }
  };

  const handleChange = async (field) => {
    if (!field) return;
    const payload = {};
    Object.entries(field).forEach(([key, value]) => {
      setErrorMessages((prev) => ({ ...prev, [key]: '' }));
      const prev = survey[key];

      if (value !== prev && (value !== '' || key === 'description')) {
        payload[key] = value;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    try {
      await SurveyService.update(survey.id, payload);
    } catch (error) {
      let err = error;
      if (error.isAxiosError) {
        err = formatError(error);
        const [key] = Object.entries(field)[0];
        setErrorMessages((p) => ({ ...p, [key]: err.message }));
      }
    } finally {
      await fetchSurvey();
    }
  };

  const handleRedirectParametersChange = async (fieldIds) => {
    try {
      setRedirectParameterFieldIds(fieldIds);
      await handleChange({
        redirectParameterFieldIds: fieldIds,
      });
    } catch (error) {
      const orig = getOriginalError(error);
      if (orig?.message) {
        showToast({
          message: orig?.message,
          variant: toastVariant.FAILURE,
        });
      } else if (error.isAxiosError) {
        const err = formatError(error);
        setErrorMessages((prev) => ({
          ...prev,
          redirectParameterFieldIds: err.message,
        }));
      }
    }
  };

  const updateSubscribers = async (subscribers, notificationType) => {
    const selectedSubscribers = subscribers.map((item) => {
      return {
        employee: item.value,
        notificationType: item.notificationType || notificationType,
      };
    });

    await handleChange({ subscribers: selectedSubscribers });
  };

  const updateEmailSubscribers = async (subscribers) => {
    setEmailTeamMembers(
      subscribers.map((item) => {
        item.notificationType = ACTIVITY_NOTIFICATION_EMAIL;
        return item;
      })
    );
    await updateSubscribers(
      [...subscribers, ...smsTeamMembers],
      ACTIVITY_NOTIFICATION_EMAIL
    );
  };

  const updateSmsSubscribers = async (subscribers) => {
    setSmsTeamMembers(
      subscribers.map((item) => {
        item.notificationType = ACTIVITY_NOTIFICATION_SMS;
        return item;
      })
    );
    await updateSubscribers(
      [...subscribers, ...emailTeamMembers],
      ACTIVITY_NOTIFICATION_SMS
    );
  };

  const shouldBlockNavigation = Boolean(
    errorMessages.name ||
      (submissionAction === SUBMISSION_ACTIONS.URL &&
        (errorMessages.redirectUrl || !redirectUrl))
  );

  const handleBlockNavigation = (location, action) => {
    if (allowNavigation(location, action)) {
      return true;
    }

    if (shouldBlockNavigation) {
      setErrorMessages((prev) => ({
        ...prev,
        name: prev.name ? NAME_NAVIGATE_AWAY_MESSAGE(t) : prev.name,
        redirectUrl:
          submissionAction === SUBMISSION_ACTIONS.URL &&
          (prev.redirectUrl || !redirectUrl)
            ? REDIRECT_URL_NAVIGATE_AWAY_MESSAGE_EMPTY(t)
            : prev.redirectUrl,
      }));
    }

    return false;
  };
  const chosenEmailValueIds = useMemo(
    () => emailTeamMembers.map(({ value }) => value),
    [emailTeamMembers]
  );
  const chosenSMSValueIds = useMemo(
    () => smsTeamMembers.map(({ value }) => value),
    [smsTeamMembers]
  );

  const [emailNotificationSelectProps] = useSelectTypeaheadWithScroll({
    selectRef: emailSelectRef,
    objectToOption: convertToTeamOption,
    entity: Entities.TeamMember,
    chosenValueIds: chosenEmailValueIds,
  });
  const [smsNotificationSelectProps] = useSelectTypeaheadWithScroll({
    selectRef: smsSelectRef,
    objectToOption: convertToTeamOption,
    entity: Entities.TeamMember,
    chosenValueIds: chosenSMSValueIds,
  });

  const handleOnSwitchChange = async (ev) => {
    setPassVariablesOnRedirect(ev.target.checked);
    setRedirectParameterFieldIds([]);
    await handleChange({
      passVariablesOnRedirect: ev.target.checked,
      redirectParameterFieldIds: [],
    });
  };

  const handleChangeDescription = async (description) => {
    setErrorMessages((prev) => ({ ...prev, description: '' }));
    await handleChange({ description });
  };

  return (
    <>
      <Prompt when={shouldBlockNavigation} message={handleBlockNavigation} />
      <PageSizing>
        <StyledSettingsContentCard>
          <SubSection title={t('Survey Settings')}>
            <Cols columns={3} gutter={`${gutters.spacing(4)}px`}>
              <InputControl margin>
                <SurveyNameInput
                  errorMessage={errorMessages.name}
                  value={name}
                  onChange={setName}
                  onBlur={async () => {
                    if (name === '') {
                      setErrorMessages((prev) => ({
                        ...prev,
                        name: NO_NAME_MESSAGE(t),
                      }));
                    } else {
                      await handleChange({ name });
                    }
                  }}
                />
              </InputControl>
              <TextInput
                disabled
                label={t('API Name')}
                value={survey.apiName}
              />
              <StaticLink
                label={t('Survey Object')}
                linkLabel={survey.relatedObject.objectName}
                link={`/custom-objects/${survey.relatedObject.id}`}
              />
            </Cols>
            <StyledCols columns={3} gutter={`${gutters.spacing(4)}px`}>
              <Select
                margin
                fullWidth
                label={t('Default Submission Action')}
                value={submissionAction}
                options={submissionOptions(t)}
                onChange={async (opt) => {
                  setSubmissionAction(opt.value);
                  await handleChange({ submissionAction: opt.value });
                }}
              />
              {submissionAction === SUBMISSION_ACTIONS.URL && (
                <RedirectUrlInput
                  redirectUrl={redirectUrl}
                  passVariablesOnRedirect={passVariablesOnRedirect}
                  redirectParameterFieldIds={redirectParameterFieldIds}
                  errorMessage={errorMessages.redirectUrl}
                  onTextChange={setRedirectUrl}
                  onTextBlur={handleRedirectUrlChange}
                  onSwitchChange={handleOnSwitchChange}
                  onRedirectParametersChange={handleRedirectParametersChange}
                  fields={fields}
                  loading={fieldsLoading}
                />
              )}
            </StyledCols>
            <StyledCols columns={2}>
              <StyleInputControlLink label={t('Hosted Survey Link')} margin>
                <KizenTypography
                  type="link-mail"
                  target="_blank"
                  href={previewLink}
                >
                  {previewLink}
                </KizenTypography>
              </StyleInputControlLink>
            </StyledCols>
            <Description
              label={t('Survey Description')}
              description={survey?.description}
              aiDescription={survey?.aiDescription}
              onChange={handleChangeDescription}
              inputProps={{
                margin: true,
              }}
              error={errorMessages.description}
            />
            <InputControl label={t('Website Embed Code')} margin>
              <Notice>
                <KizenTypography>
                  {t(
                    'Use to embed your survey within an existing website. Insert this code where you would like the survey to appear.'
                  )}
                </KizenTypography>
              </Notice>
            </InputControl>
            <InputControl>
              <StyledLongText
                value={webApplicationTrackerEmbed(
                  business,
                  `KIZEN('embedForm', '${chosenBusinessUrl}/survey/${survey.slug}')`
                )}
                readOnly
              />
            </InputControl>
          </SubSection>
        </StyledSettingsContentCard>
        <StyledSettingsContentCard>
          <SubSection title={t('Survey Availability')}>
            <Cols
              columns={1}
              style={{ marginBottom: availability.enabled ? '0px' : '20px' }}
            >
              <Switch
                checked={availability.enabled}
                label={t('Enable Survey')}
                textPlacement="regular-top"
                tooltip={t(
                  'This will prevent new Survey submissions and redirect Survey visitors elsewhere'
                )}
                onChange={async (event) => {
                  const previous = availability;
                  const enabled = event.target.checked;

                  try {
                    if (enabled) {
                      setAvailability({
                        enabled: true,
                        disabledAction: null,
                        disabledTargetForm: null,
                        disabledTargetFormSearch: '',
                        disabledTargetUrl: '',
                      });

                      await SurveyService.update(survey.id, {
                        enabled: true,
                        disabledAction: null,
                        disabledTargetUrl: '',
                        disabledTargetForm: null,
                      });

                      showToast({
                        message: t('Survey ‘{{name}}’ has been enabled.', {
                          name,
                        }),
                      });
                    } else {
                      setAvailability({
                        enabled: false,
                        disabledAction: AVAILABILITY_ACTION.GO_TO_NOT_FOUND,
                        disabledTargetForm: null,
                        disabledTargetFormSearch: '',
                        disabledTargetUrl: '',
                      });

                      await SurveyService.update(survey.id, {
                        enabled: false,
                        disabledAction: AVAILABILITY_ACTION.GO_TO_NOT_FOUND,
                      });

                      showToast({
                        message: t('Survey ‘{{name}}’ has been disabled.', {
                          name,
                        }),
                      });
                    }
                  } catch (err) {
                    setAvailability(previous);
                    showToast({
                      variant: toastVariant.FAILURE,
                      message: t(
                        'There was an issue {{action}} the survey. Please try again.',
                        { action: enabled ? t('enabling') : t('disabling') }
                      ),
                    });
                  }
                }}
              />
            </Cols>
            {!availability.enabled && (
              <Cols columns={2}>
                <Select
                  value={availability.disabledAction}
                  label={t('Redirect Survey Link Visitors')}
                  options={[
                    {
                      value: AVAILABILITY_ACTION.GO_TO_NOT_FOUND,
                      label: t('Go to Page Not Found'),
                    },
                    {
                      value: AVAILABILITY_ACTION.GO_TO_URL,
                      label: t('Go to URL'),
                    },
                    {
                      value: AVAILABILITY_ACTION.GO_TO_FORM,
                      label: t('Go to Another Survey'),
                    },
                  ]}
                  onChange={async (option) => {
                    const previous = availability;

                    try {
                      setAvailability((prev) => {
                        return {
                          ...prev,
                          disabledAction: option.value,
                          disabledTargetForm: null,
                          disabledTargetUrl: '',
                        };
                      });

                      if (
                        option.value === AVAILABILITY_ACTION.GO_TO_NOT_FOUND
                      ) {
                        await SurveyService.update(survey.id, {
                          disabledAction: option.value,
                          disabledTargetForm: null,
                          disabledTargetUrl: '',
                        });
                      }
                    } catch (err) {
                      setAvailability(previous);
                      showToast({
                        variant: toastVariant.FAILURE,
                        message: t(
                          'There was an issue updating the survey. Please try again.'
                        ),
                      });
                    }
                  }}
                />
                {availability.disabledAction ===
                  AVAILABILITY_ACTION.GO_TO_URL && (
                  <>
                    <TextInput
                      ref={availabilityTargetUrlRef}
                      value={availability.disabledTargetUrl}
                      label={t('Redirect URL')}
                      placeholder={t('Enter URL')}
                      error={Boolean(errorMessages.availabilityTargetUrl)}
                      onChange={(value) => {
                        setErrorMessages((prev) => {
                          return { ...prev, availabilityTargetUrl: null };
                        });
                        setAvailability((prev) => {
                          return { ...prev, disabledTargetUrl: value };
                        });
                      }}
                      onBlur={async () => {
                        const url = availability.disabledTargetUrl.trim();

                        if (url.length === 0) {
                          setErrorMessages((prev) => ({
                            ...prev,
                            availabilityTargetUrl:
                              REQUIRED_REDIRECT_URL_MESSAGE(t),
                          }));
                        } else if (
                          !(
                            url.startsWith('http://') ||
                            url.startsWith('https://')
                          )
                        ) {
                          setErrorMessages((prev) => ({
                            ...prev,
                            availabilityTargetUrl:
                              MISSING_URL_PROTOCOL_MESSAGE(t),
                          }));
                        } else if (!checkUrl(url)) {
                          setErrorMessages((prev) => ({
                            ...prev,
                            availabilityTargetUrl: MISSING_URL_TLD_MESSAGE(t),
                          }));
                        } else {
                          try {
                            await SurveyService.update(survey.id, {
                              disabledAction: AVAILABILITY_ACTION.GO_TO_URL,
                              disabledTargetUrl: url,
                            });
                          } catch (err) {
                            if (err.response?.data?.disabled_target_url?.[0]) {
                              setErrorMessages((prev) => ({
                                ...prev,
                                availabilityTargetUrl:
                                  err.response.data.disabled_target_url[0],
                              }));
                            } else {
                              showToast({
                                variant: toastVariant.FAILURE,
                                message: t(
                                  'There was an issue updating the survey. Please try again.'
                                ),
                              });
                            }
                          }
                        }
                      }}
                    />
                    {Boolean(errorMessages.availabilityTargetUrl) && (
                      <Overlay
                        show
                        placement="bottom-start"
                        target={availabilityTargetUrlRef.current}
                      >
                        <ErrorCard show>
                          <KizenTypography>
                            {errorMessages.availabilityTargetUrl}
                          </KizenTypography>
                        </ErrorCard>
                      </Overlay>
                    )}
                  </>
                )}
                {availability.disabledAction ===
                  AVAILABILITY_ACTION.GO_TO_FORM && (
                  <Select
                    value={availability.disabledTargetForm}
                    label={t('Target Survey')}
                    placeholder={t('Choose Survey')}
                    validate={targetFormValidate}
                    inputValue={availability.disabledTargetFormSearch}
                    options={surveyOptionsQuery.data?.options ?? []}
                    onInputChange={(value) => {
                      setAvailability((prev) => {
                        return { ...prev, disabledTargetFormSearch: value };
                      });
                    }}
                    onChange={async (option) => {
                      const previous = availability;

                      try {
                        setTargetFormValidate({
                          showMessage: false,
                          message: '',
                        });
                        setAvailability((prev) => {
                          return { ...prev, disabledTargetForm: option.value };
                        });

                        await SurveyService.update(survey.id, {
                          disabledAction: AVAILABILITY_ACTION.GO_TO_FORM,
                          disabledTargetForm: option.value,
                        });
                      } catch (err) {
                        setAvailability(previous);
                        showToast({
                          variant: toastVariant.FAILURE,
                          message: t(
                            'There was an issue updating the survey. Please try again.'
                          ),
                        });
                      }
                    }}
                  />
                )}
              </Cols>
            )}
          </SubSection>
        </StyledSettingsContentCard>
        <SettingsContentCard>
          <SubSection title={t('Notifications')}>
            <Notice style={{ marginBottom: `${gutters.spacing(4)}px` }}>
              <KizenTypography as="span">
                {t(
                  'Notify team members through text or email each time this survey is submitted.'
                )}
              </KizenTypography>
            </Notice>
            <Cols columns={2} gutter={`${gutters.spacing(4)}px`}>
              <MultiSelect
                ref={emailSelectRef}
                label={t('Notify Team Members via Email')}
                placeholder={t('Find Team Members')}
                value={emailTeamMembers}
                onChange={updateEmailSubscribers}
                margin="normal"
                {...emailNotificationSelectProps}
              />
              <MultiSelect
                ref={smsSelectRef}
                label={t('Notify Team Members via Text')}
                placeholder={t('Find Team Members')}
                value={smsTeamMembers}
                onChange={updateSmsSubscribers}
                {...smsNotificationSelectProps}
              />
            </Cols>
          </SubSection>
          <div />
        </SettingsContentCard>
      </PageSizing>
    </>
  );
};

export default SettingsPage;
